import { getBloomColorHex } from '../scene3d/plantArchetypes';

/**
 * bloomUtils - Reine Blüh-Logik für die Monatsansicht (#270 / #608)
 *
 * Keine Fabric-/DOM-Abhängigkeit: nur Pflanzen-Properties → Blüh-Aussage.
 * Die „eine Farbwahrheit" (`getBloomColorHex`) lebt in scene3d/plantArchetypes
 * (für die 3D-Blüh-Schicht #601 gebaut) und wird hier nur re-exportiert —
 * nicht neu geschrieben.
 */

// Re-export der bestehenden Farbwahrheit (selectedColor gewinnt, sonst DB-flower_color).
export { getBloomColorHex };

/** Property flach oder unter plant.properties lesen (beide API-Formate). */
function plantProp(plant, key) {
  const flat = plant?.[key];
  if (flat !== undefined && flat !== null) return flat;
  return plant?.properties?.[key] ?? null;
}

/**
 * Blüht die Pflanze im gegebenen Monat?
 *
 * @param {object} plantProperties - PlantCircle.plantProperties (flach oder .properties)
 * @param {number} month - 0-indiziert (0 = Januar … 11 = Dezember), wie Date.getMonth()
 * @returns {boolean|null} true/false, oder `null` wenn keine Blühangabe vorliegt
 *   (eigener neutraler Zustand — bewusst NICHT „blüht nicht", vgl. 7 Pflanzen ohne Daten)
 */
export function isBloomingInMonth(plantProperties, month) {
  const months = plantProp(plantProperties, 'flowering_months');
  if (!Array.isArray(months) || months.length === 0) {
    return null; // keine Angabe → neutraler Zustand
  }
  const oneBased = normalizeMonth(month) + 1; // flowering_months ist 1–12
  return months.some((m) => Number(m) === oneBased);
}

/** Monat robust auf 0–11 bringen (Wrap für Werte außerhalb des Bereichs). */
function normalizeMonth(month) {
  const n = Math.trunc(Number(month));
  if (!Number.isFinite(n)) return 0;
  return ((n % 12) + 12) % 12;
}

/** Anzeigename einer Pflanze (Art-Ebene). */
function plantDisplayName(plantProperties) {
  return (
    plantProp(plantProperties, 'name') ||
    plantProp(plantProperties, 'german_name') ||
    'Unbekannte Pflanze'
  );
}

/** Dedup-Schlüssel je Art: bevorzugt den botanischen Namen. */
function speciesKey(plantProperties) {
  return (
    plantProp(plantProperties, 'scientific_name') || plantDisplayName(plantProperties)
  );
}

/**
 * Blühkalender über alle 12 Monate aggregieren (Basis für Jahresleiste & Dashboard).
 *
 * Pro Monat werden die blühenden Pflanzen **nach Art dedupliziert** (drei
 * Lavendel = ein Eintrag) und mit ihrer Blütenfarbe geführt. Pflanzen ohne
 * Blühangabe (`isBloomingInMonth` → null) zählen nirgends mit.
 *
 * @param {Array<{plantProperties: object, selectedColor?: string}>} plants
 * @returns {Array<{month: number, species: Array<{key: string, name: string, color: string|null}>, count: number}>}
 *   genau 12 Einträge (Index = Monat, 0-basiert)
 */
export function aggregateBloomCalendar(plants) {
  const months = Array.from({ length: 12 }, (_, month) => ({
    month,
    byKey: new Map(),
  }));

  for (const obj of plants || []) {
    const props = obj?.plantProperties;
    if (!props) continue;
    const key = speciesKey(props);
    const name = plantDisplayName(props);
    const color = getBloomColorHex(props, obj.selectedColor);

    for (let month = 0; month < 12; month++) {
      if (isBloomingInMonth(props, month) === true) {
        const bucket = months[month].byKey;
        if (!bucket.has(key)) bucket.set(key, { key, name, color });
      }
    }
  }

  return months.map(({ month, byKey }) => {
    const species = Array.from(byKey.values());
    return { month, species, count: species.length };
  });
}

/**
 * Welche der platzierten Pflanzen blühen in einem Monat? (Dashboard-Karte #609)
 *
 * Verbindet die (evtl. nur als IDs vorliegenden) platzierten Pflanzen mit dem
 * vollen Katalog und dedupliziert nach Art. Robust gegenüber beiden Formen von
 * `placedPlants`: bloße IDs, `{ id/plantId }` oder volle Canvas-Objekte
 * (deren `selectedColor` dann für die Farbe gewinnt).
 *
 * @param {Array} placedPlants - IDs oder Objekte der platzierten Pflanzen
 * @param {Array<object>} allPlants - voller Pflanzenkatalog (mit `id`)
 * @param {number} month - 0-basiert (0 = Januar)
 * @returns {Array<{id: any, name: string, color: string|null}>} deduplizierte, blühende Arten
 */
export function collectNowBlooming(placedPlants, allPlants, month) {
  if (!Array.isArray(placedPlants) || !Array.isArray(allPlants)) return [];
  const byId = new Map(allPlants.map((p) => [p.id, p]));
  const seen = new Map();

  for (const entry of placedPlants) {
    const isObj = typeof entry === 'object' && entry !== null;
    const id = isObj ? entry.id ?? entry.plantId ?? null : entry;
    if (id == null) continue;
    const record = byId.get(id);
    if (!record) continue;
    if (isBloomingInMonth(record, month) !== true) continue;

    const key = speciesKey(record);
    if (seen.has(key)) continue;
    const selectedColor = isObj ? entry.selectedColor ?? null : null;
    seen.set(key, {
      id,
      name: plantDisplayName(record),
      color: getBloomColorHex(record, selectedColor),
    });
  }

  return Array.from(seen.values());
}
